import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Users,
  Clock,
  Coffee,
  TrendingUp,
  ArrowRight,
  Loader2,
  UserCheck,
  LogOut as LogOutIcon,
  Building2,
  Award,
  AlertTriangle,
  FileText,
  DollarSign,
  Edit
} from "lucide-react";
import { format } from "date-fns";
import { RecentIncidentsCard } from "../components/RecentIncidentsCard";
import { TimeIssuesCard } from "../components/TimeIssuesCard";

export default function AdminDashboard() {
  const { authAxios, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());

  const fetchStats = useCallback(async () => {
    try {
      const res = await authAxios.get('/admin/dashboard-stats');
      setStats(res.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => {
    fetchStats();
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    const refreshInterval = setInterval(fetchStats, 60000);
    return () => {
      clearInterval(interval);
      clearInterval(refreshInterval);
    };
  }, [fetchStats]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Employees',
      value: stats?.total_employees || 0,
      icon: Users,
      color: 'bg-blue-100 text-blue-600',
      bgColor: 'from-blue-50 to-blue-100/50'
    },
    {
      title: 'Currently Working',
      value: stats?.currently_working || 0,
      icon: UserCheck,
      color: 'bg-emerald-100 text-emerald-600',
      bgColor: 'from-emerald-50 to-emerald-100/50'
    },
    {
      title: 'On Break',
      value: stats?.on_break || 0,
      icon: Coffee,
      color: 'bg-amber-100 text-amber-600',
      bgColor: 'from-amber-50 to-amber-100/50'
    },
    {
      title: 'Clocked Out Today',
      value: stats?.clocked_out_today || 0,
      icon: LogOutIcon,
      color: 'bg-stone-100 text-stone-600',
      bgColor: 'from-stone-50 to-stone-100/50'
    }
  ];

  const formatCurrency = (amount) => `$${(amount || 0).toFixed(2)}`;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-dashboard">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-stone-900 font-heading">Admin Dashboard</h1>
            <p className="text-stone-500 mt-1">
              {format(currentTime, 'EEEE, MMMM d, yyyy')} • {format(currentTime, 'HH:mm:ss')}
            </p>
          </div>
          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 px-4 py-2 text-sm w-fit">
            <Clock className="w-4 h-4 mr-2" />
            Live Dashboard
          </Badge>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat, index) => (
          <Card key={index} className={`border-0 shadow-lg shadow-stone-200/50 bg-gradient-to-br ${stat.bgColor} overflow-hidden`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-stone-500 font-medium">{stat.title}</p>
                  <p className="text-4xl font-bold text-stone-900 mt-2 font-mono">{stat.value}</p>
                </div>
                <div className={`w-14 h-14 ${stat.color} rounded-2xl flex items-center justify-center`}>
                  <stat.icon className="w-7 h-7" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Currently Working - with Projects */}
        <Card className="border-0 shadow-lg shadow-stone-200/50 bg-white">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-emerald-600" />
              Currently Working
            </CardTitle>
            <Link to="/admin/records">
              <Button variant="ghost" size="sm" className="text-emerald-600 hover:text-emerald-700">
                View All <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {stats?.working_employees?.length === 0 ? (
              <div className="text-center py-8 text-stone-400">
                <UserCheck className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>No employees currently working</p>
              </div>
            ) : (
              <div className="space-y-3">
                {stats?.working_employees?.slice(0, 5).map((emp) => (
                  <div key={emp.id} className={`flex items-center gap-3 p-3 rounded-xl ${emp.status === 'on_break' ? 'bg-amber-50' : 'bg-emerald-50'}`}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${emp.status === 'on_break' ? 'bg-amber-500' : 'bg-emerald-600'}`}>
                      {emp.full_name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-stone-900">{emp.full_name}</p>
                      {emp.project_name && (
                        <p className="text-xs text-stone-500 flex items-center gap-1">
                          <Building2 className="w-3 h-3" />{emp.project_name}
                        </p>
                      )}
                    </div>
                    <Badge className={emp.status === 'on_break' ? 'bg-amber-100 text-amber-800 border-amber-200' : 'bg-emerald-100 text-emerald-800 border-emerald-200'}>
                      {emp.status === 'on_break' ? 'Break' : 'Working'}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Expiring Certifications */}
        <Card className="border-0 shadow-lg shadow-stone-200/50 bg-white">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-600" />
              Certification Alerts
            </CardTitle>
            <Link to="/admin/certifications">
              <Button variant="ghost" size="sm" className="text-amber-600 hover:text-amber-700">
                View All <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {stats?.expiring_certifications?.length === 0 ? (
              <div className="text-center py-8 text-stone-400">
                <Award className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>All certifications up to date</p>
              </div>
            ) : (
              <div className="space-y-3">
                {stats?.expiring_certifications?.slice(0, 5).map((cert) => (
                  <div key={cert.id} className={`flex items-center gap-3 p-3 rounded-xl ${cert.status === 'expired' ? 'bg-rose-50' : 'bg-amber-50'}`}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${cert.status === 'expired' ? 'bg-rose-100' : 'bg-amber-100'}`}>
                      {cert.status === 'expired' ? (
                        <AlertTriangle className="w-5 h-5 text-rose-600" />
                      ) : (
                        <Clock className="w-5 h-5 text-amber-600" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-stone-900">{cert.employee_name}</p>
                      <p className="text-xs text-stone-500">{cert.cert_name}</p>
                    </div>
                    <Badge variant="outline" className={cert.status === 'expired' ? 'bg-rose-100 text-rose-800 border-rose-200' : 'bg-amber-100 text-amber-800 border-amber-200'}>
                      {cert.days_until_expiry > 0 ? `${cert.days_until_expiry}d` : cert.days_until_expiry === 0 ? 'Today' : 'Expired'}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <TimeIssuesCard />
      <RecentIncidentsCard />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Project Labour Costs */}
        <Card className="border-0 shadow-lg shadow-stone-200/50 bg-white">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-blue-600" />
              Project Labour Costs
            </CardTitle>
            <Link to="/admin/projects">
              <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-700">
                View All <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {stats?.project_summaries?.length === 0 ? (
              <div className="text-center py-8 text-stone-400">
                <Building2 className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>No project data yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {stats?.project_summaries?.map((proj) => (
                  <div key={proj.id} className="flex items-center gap-3 p-3 bg-blue-50 rounded-xl">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-stone-900">{proj.name}</p>
                      <p className="text-xs text-stone-500">{proj.total_hours}h logged</p>
                    </div>
                    <p className="font-mono font-bold text-blue-700">{formatCurrency(proj.total_cost)}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="border-0 shadow-lg shadow-stone-200/50 bg-white">
          <CardHeader>
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link to="/admin/employees" className="block">
              <Button variant="outline" className="w-full justify-start h-14 text-left rounded-xl border-stone-200 hover:bg-emerald-50 hover:border-emerald-200 transition-all">
                <Users className="w-5 h-5 mr-3 text-emerald-600" />
                <div>
                  <p className="font-medium">Manage Employees</p>
                  <p className="text-xs text-stone-400">Add, edit, set hourly rates</p>
                </div>
              </Button>
            </Link>
            <Link to="/admin/records" className="block" data-testid="quick-action-edit-hours">
              <Button variant="outline" className="w-full justify-start h-14 text-left rounded-xl border-stone-200 hover:bg-rose-50 hover:border-rose-200 transition-all">
                <Edit className="w-5 h-5 mr-3 text-rose-600" />
                <div>
                  <p className="font-medium">Edit Hours</p>
                  <p className="text-xs text-stone-400">Fix forgotten clock-ins / outs</p>
                </div>
              </Button>
            </Link>
            <Link to="/admin/projects" className="block">
              <Button variant="outline" className="w-full justify-start h-14 text-left rounded-xl border-stone-200 hover:bg-blue-50 hover:border-blue-200 transition-all">
                <Building2 className="w-5 h-5 mr-3 text-blue-600" />
                <div>
                  <p className="font-medium">Manage Projects</p>
                  <p className="text-xs text-stone-400">Track labour costs by site</p>
                </div>
              </Button>
            </Link>
            <Link to="/admin/certifications" className="block">
              <Button variant="outline" className="w-full justify-start h-14 text-left rounded-xl border-stone-200 hover:bg-amber-50 hover:border-amber-200 transition-all">
                <Award className="w-5 h-5 mr-3 text-amber-600" />
                <div>
                  <p className="font-medium">Certifications</p>
                  <p className="text-xs text-stone-400">Track expiry dates</p>
                </div>
              </Button>
            </Link>
            <Link to="/admin/documents" className="block">
              <Button variant="outline" className="w-full justify-start h-14 text-left rounded-xl border-stone-200 hover:bg-purple-50 hover:border-purple-200 transition-all">
                <FileText className="w-5 h-5 mr-3 text-purple-600" />
                <div>
                  <p className="font-medium">Documents & Templates</p>
                  <p className="text-xs text-stone-400">Create with saved lists</p>
                </div>
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
